import React, { useMemo } from 'react';
import { SivaContractOverviewDto, SivaPhase } from 'src/api/v2';
import DataTableV3, { DataTableV3Actions } from 'src/components/DataTableV3';
import { DataTableV3Ids } from 'src/constants/dataTableIds';
import { useTranslation } from 'react-i18next';
import { useSivaContracts } from '../hooks/use-siva-contracts';
import { ColumnDef } from '@tanstack/react-table';
import { advisorColumnAccessor } from '../hooks/use-siva-overview-columns';
import styles from './SivaContractOverview.module.scss';
import ResetFilterButton from 'src/components/ResetFilterButton/ResetFilterButton';
import QueryParamMultiSelect from 'src/components/Filters/QueryParamMultiSelect';
import { useSearchParams } from 'react-router-dom';
import FilterDateInput from 'src/components/Filters/FilterDateInput';
import { TabDefinition } from '../hooks/use-siva-overview-tabs';
import { useAuth } from 'src/features/authorization';

type Props = {
  availablePhases: SivaPhase[];
  columns: ColumnDef<SivaContractOverviewDto>[];
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRows: SivaContractOverviewDto[];
  setSelectedRows: React.Dispatch<React.SetStateAction<SivaContractOverviewDto[]>>;
  activeTabName: TabDefinition['name'];
};

const SivaContractOverviewDataTable: React.FC<Props> = ({
  columns,
  availablePhases,
  setShowDialog,
  selectedRows,
  setSelectedRows,
  activeTabName,
}) => {
  const { filtersElement, contracts, isLoading } = useContractsWithFilters({ availablePhases });

  const { t: tSiva } = useTranslation('siva');

  const ideasActions: DataTableV3Actions<SivaContractOverviewDto> = {
    secondary: [
      {
        icon: { prefix: 'fal', iconName: 'circle-xmark' },
        label: tSiva('ideasAndCompanies.terminateChosenIdeas'),
        onClick: () => setShowDialog(true),
        disabled: selectedRows.length == 0,
      },
    ],
  };

  const alumniActions: DataTableV3Actions<SivaContractOverviewDto> = {
    secondary: [
      {
        icon: { prefix: 'fal', iconName: 'folder-open' },
        label: tSiva('ideasAndCompanies.reopenChosen'),
        onClick: () => setShowDialog(true),
        disabled: selectedRows.length == 0,
      },
    ],
  };

  const getActionsByActiveTab = () => {
    switch (activeTabName) {
      case 'alumni':
        return alumniActions;
      case 'ideas':
        return ideasActions;
    }
  };

  return (
    <DataTableV3
      id={DataTableV3Ids.Siva.IdeasAndCompanies.ContractsOverview}
      data={contracts}
      pageSizeOptions={[25, 50, 100, 250, 500]}
      loading={isLoading}
      pageSize={100}
      columns={columns}
      sorting={[
        {
          id: 'numericId',
          desc: true,
        },
      ]}
      customColumnFilters={filtersElement}
      showRowsSelectedText
      hideFilterText
      actions={getActionsByActiveTab()}
      setSelectedRows={setSelectedRows}
    />
  );
};

interface useContractsWithFiltersParams {
  availablePhases: SivaPhase[];
}

const useContractsWithFilters = ({ availablePhases }: useContractsWithFiltersParams) => {
  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');

  const { checkAccess } = useAuth();
  const isBusinessGarden = checkAccess('UI_SivaBusinessGarden').read;

  const phaseFilterOptions = useMemo(
    () => [
      ...availablePhases.map((phase) => ({
        value: phase,
        label: tSiva(`phase.${phase}` as any, {
          defaultValue: phase,
        }),
      })),
    ],
    [availablePhases, tSiva],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const { phaseFilter, advisorFilter, fromDate, toDate, contractTypeFilter } = useMemo(() => {
    return {
      phaseFilter: (searchParams.get('p')?.split(',') as SivaPhase[]) ?? ([] as SivaPhase[]),
      advisorFilter: searchParams.get('advisor')?.split(',') ?? [],
      fromDate: searchParams.get('from') ?? '',
      toDate: searchParams.get('to') ?? '',
      contractTypeFilter: searchParams.get('contract-type')?.split(',') ?? [],
    };
  }, [searchParams]);

  const { contracts, isLoading } = useSivaContracts({
    sivaPhases: availablePhases,
  });

  const advisorOptions = useMemo(() => {
    const sortedUniqueNames = [
      ...new Set(
        contracts
          .filter((contract) => contract.advisorLastName && contract.advisorFirstName)
          .map(advisorColumnAccessor),
      ),
    ].sort();
    return [
      ...sortedUniqueNames.map((value) => ({
        value,
        label: value,
      })),
    ];
  }, [contracts]);

  const contractTypeOptions = [
    {
      value: 'yes',
      label: tCommon('yes'),
    },
    {
      value: 'no',
      label: tCommon('no'),
    },
  ];

  const filtersElement = (
    <div className={styles.filters}>
      {availablePhases.length > 1 && (
        <div className="width--25">
          <QueryParamMultiSelect
            name="idea-filter"
            label={tCommon('phase')}
            queryStringParam="p"
            options={phaseFilterOptions}
            placeHolder="Velg fase"
          />
        </div>
      )}
      <div className="width--25">
        <QueryParamMultiSelect
          queryStringParam="advisor"
          name="advisor-filter"
          label={tCommon('advisor')}
          options={advisorOptions}
          placeHolder="Velg rådgiver"
        />
      </div>
      {availablePhases.length === 1 && availablePhases[0] === SivaPhase.Alumni && (
        <>
          {!isBusinessGarden && (
            <div className="width--25">
              <QueryParamMultiSelect
                queryStringParam="contract-type"
                name="contract-type-filter"
                label={tSiva('hasOnlyBeenIdea')}
                options={contractTypeOptions}
              />
            </div>
          )}
          <div className="width--25">
            <FilterDateInput queryStringParam={'from'} label={tCommon('fromDate')} />
          </div>
          <div className="width--25">
            <FilterDateInput queryStringParam={'to'} label={tCommon('toDate')} />
          </div>
        </>
      )}

      <ResetFilterButton
        onClick={() => setSearchParams(undefined, { replace: true })}
        className={styles.resetButton}
      />
    </div>
  );

  const filteredContracts = useMemo(() => {
    const filterByPhase = (item: SivaContractOverviewDto): boolean => {
      if (phaseFilter.length > 0) {
        return phaseFilter.includes(item.phase as SivaPhase);
      }
      return true;
    };
    const filterByAdvisor = (item: SivaContractOverviewDto): boolean => {
      if (advisorFilter.length > 0) {
        return advisorFilter.includes(`${item.advisorFirstName} ${item.advisorLastName}`.trim());
      }
      return true;
    };
    const filterByDateRange = (item: SivaContractOverviewDto): boolean => {
      if (item?.fromDate === undefined) return false;
      const submittedDate = new Date(item?.fromDate);
      return (
        (fromDate ? submittedDate >= new Date(fromDate) : true) &&
        (toDate ? submittedDate <= new Date(toDate) : true)
      );
    };
    const filterByContractType = (item: SivaContractOverviewDto): boolean => {
      if (contractTypeFilter.length < 1 || item.hasOnlyBeenIdeaOrAlumni == undefined) return true;
      if (contractTypeFilter.includes('yes') && item.hasOnlyBeenIdeaOrAlumni) return true;
      if (contractTypeFilter.includes('no') && !item.hasOnlyBeenIdeaOrAlumni) return true;

      return false;
    };
    return contracts
      .filter(filterByPhase)
      .filter(filterByAdvisor)
      .filter(filterByDateRange)
      .filter(filterByContractType);
  }, [advisorFilter, contracts, fromDate, phaseFilter, toDate, contractTypeFilter]);

  return {
    filtersElement,
    contracts: filteredContracts,
    isLoading,
  };
};

export default SivaContractOverviewDataTable;
